// platform.js

// 平台类型枚举
export const PlatformType = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
  WECHAT_BROWSER: 'wechat_browser',
  WECHAT_MINI_PROGRAM: 'wechat_mini_program',
  ALIPAY: 'alipay',
  DOUYIN: 'douyin',
  IOS_WEBVIEW: 'ios_webview',
  ANDROID_WEBVIEW: 'android_webview',
  UNKNOWN: 'unknown'
};

// 设备类型枚举
export const DeviceType = {
  IOS: 'ios',
  ANDROID: 'android',
  WINDOWS: 'windows',
  MAC: 'mac',
  LINUX: 'linux',
  UNKNOWN: 'unknown'
};

// 缓存检测结果
let cachedResult = null;

// 用户代理字符串
const ua = navigator.userAgent;

// 具体检测方法
function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

function isWeChatBrowser() {
  return /MicroMessenger/i.test(ua);
}

function isWeChatMiniProgram() {
  // 检查是否在微信小程序环境中
  try {
    return (typeof wx !== 'undefined' && wx && wx.miniProgram) || /miniProgram/i.test(ua);
  } catch (error) {
    // 在某些环境下访问wx可能会抛出异常，安全处理
    return /miniProgram/i.test(ua);
  }
}

function isWebView() {
  if (typeof window !== 'undefined' && window) {
    // 常见的APP WebView标识
    if (window.AndroidBridge || 
        window.webkit?.messageHandlers || 
        window.JSBridge ||
        window.ReactNativeWebView ||
        window.flutter_inappwebview) {
      return true;
    }
  }
  return false;
}

function getSpecificWebView() {
  if (/AlipayClient/i.test(ua)) { return 'alipay'; }
  if (/aweme/i.test(ua)) { return 'douyin'; }
  return null;
}

function isInIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

/**
 * 获取完整的平台检测信息
 * @returns {Object} 平台检测结果对象
 */
export function detect() {
  if (cachedResult) { return cachedResult; }

  const result = {
    platform: PlatformType.UNKNOWN,
    device: DeviceType.UNKNOWN,
    isInIframe: isInIframe(),
    isMobile: isMobile(),
    isDesktop: !isMobile() && (typeof window !== 'undefined' && window.innerWidth ? window.innerWidth >= 768 : true),
    isWeChatBrowser: isWeChatBrowser(),
    isWeChatMiniProgram: isWeChatMiniProgram(),
    isWebView: isWebView(),
    specificWebView: getSpecificWebView()
  };

  // 确定平台类型
  if (result.isWeChatMiniProgram) {
    result.platform = PlatformType.WECHAT_MINI_PROGRAM;
  } else if (result.isWeChatBrowser) {
    result.platform = PlatformType.WECHAT_BROWSER;
  } else if (result.specificWebView === 'alipay') {
    result.platform = PlatformType.ALIPAY;
  } else if (result.specificWebView === 'douyin') {
    result.platform = PlatformType.DOUYIN;
  } else if (result.isWebView) {
    result.platform = PlatformType.WEBVIEW;
  } else if (result.isMobile) {
    result.platform = PlatformType.MOBILE;
  } else if (result.isDesktop) {
    result.platform = PlatformType.DESKTOP;
  } else {
    result.platform = PlatformType.UNKNOWN;
  }

  // 确定设备类型
  if (/iPhone|iPad|iPod/i.test(ua)) {
    result.device = DeviceType.IOS;
  } else if (/Android/i.test(ua)) {
    result.device = DeviceType.ANDROID;
  } else if (/Windows/i.test(ua)) {
    result.device = DeviceType.WINDOWS;
  } else if (/Macintosh/i.test(ua)) {
    result.device = DeviceType.MAC;
  } else if (/Linux/i.test(ua)) {
    result.device = DeviceType.LINUX;
  }

  cachedResult = result;
  return result;
}

/**
 * 获取平台类型
 * @returns {string} 平台类型枚举值
 */
export function getPlatform() {
  // 确定平台类型
  let platform = PlatformType.UNKNOWN;
  const result = detect();
  if (result.isWeChatMiniProgram) {
    platform = PlatformType.WECHAT_MINI_PROGRAM;
  } else if (result.isWeChatBrowser) {
    platform = PlatformType.WECHAT_BROWSER;
  } else if (result.specificWebView === 'alipay') {
    platform = PlatformType.ALIPAY;
  } else if (result.specificWebView === 'douyin') {
    platform = PlatformType.DOUYIN;
  } else if (result.isWebView) {
    if (result.device === DeviceType.IOS) {
      platform = PlatformType.IOS_WEBVIEW;
    } else if (result.device === DeviceType.ANDROID) {
      platform = PlatformType.ANDROID_WEBVIEW;
    } else {
      platform = 'webview';
    }
  } else if (result.isMobile) {
    platform = PlatformType.MOBILE;
  } else if (result.isDesktop) {
    platform = PlatformType.DESKTOP;
  }
  return platform;
}

/**
 * 获取设备类型
 * @returns {string} 设备类型枚举值
 */
export function getDevice() {
  return detect().device;
}

/**
 * 是否在移动端
 * @returns {boolean}
 */
export function isMobilePlatform() {
  return detect().isMobile;
}

/**
 * 是否在桌面端
 * @returns {boolean}
 */
export function isDesktopPlatform() {
  return detect().isDesktop;
}

/**
 * 是否在微信浏览器中
 * @returns {boolean}
 */
export function inWeChatBrowser() {
  return detect().isWeChatBrowser;
}

/**
 * 是否在微信小程序中
 * @returns {boolean}
 */
export function inWeChatMiniProgram() {
  return detect().isWeChatMiniProgram;
}

/**
 * 是否在WebView中
 * @returns {boolean}
 */
export function inWebView() {
  return detect().isWebView;
}

/**
 * 是否在iframe中
 * @returns {boolean}
 */
export function inIframe() {
  return detect().isInIframe;
}

/**
 * 获取特定的WebView类型
 * @returns {string|null} 特定WebView标识或null
 */
export function getWebViewType() {
  return detect().specificWebView;
}

// 默认导出检测函数
export default {
  PlatformType,
  DeviceType,
  detect,
  getPlatform,
  getDevice,
  isMobilePlatform,
  isDesktopPlatform,
  inWeChatBrowser,
  inWeChatMiniProgram,
  inWebView,
  inIframe,
  getWebViewType
};
